// PWA install + web-push helpers. Owns the deferred beforeinstallprompt event and the
// push subscription flow, so any component (install guide, onboarding, notif prefs) can use them
// without reaching back into App.jsx.
import { api } from "./api";

let _deferred = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); _deferred = e; });
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
    || window.navigator.standalone === true;
}

// Whether the browser has offered a native install prompt (Android / desktop Chrome).
export function hasInstallPrompt() { return !!_deferred; }

// Fire the native install prompt; returns "accepted" | "dismissed" | null (no prompt available).
export async function promptInstall() {
  if (!_deferred) return null;
  _deferred.prompt();
  const res = await _deferred.userChoice;
  _deferred = null;
  return res && res.outcome;
}

export async function installApp() {
  if (!_deferred) {
    alert("Appku pridáš cez menu prehliadača → „Pridať na plochu“ (na iPhone: Zdieľať → Pridať na plochu).");
    return;
  }
  _deferred.prompt();
  await _deferred.userChoice;
  _deferred = null;
}

function urlB64ToUint8(base64) {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

// v26.3 §7 — ArrayBuffer VAPID kľúč → base64url string (na porovnanie s /api/push/key)
function bufToB64Url(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// v26.3 §7 — skúšobná notifikácia; vracia {subs, sent, errors} pre diagnostiku v Upozorneniach.
export async function testPush() {
  return await api("/api/push/test", { method: "POST" });
}

export async function enablePush() {
  try {
    // B5 — iOS delivers web-push only to an INSTALLED PWA; subscribing from a Safari tab won't
    // get background notifications. Guide the user to install first instead of failing silently.
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)
      || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    if (isIOS && !isStandalone()) {
      alert("Na iPhone/iPade fungujú upozornenia až po pridaní appky na plochu (Zdieľať → Pridať na plochu). Potom otvor appku z plochy a zapni upozornenia tam."); return;
    }
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      alert("Tvoj prehliadač nepodporuje upozornenia. Na iPhone najprv pridaj appku na plochu."); return;
    }
    const { key, enabled } = await api("/api/push/key");
    if (!enabled || !key) { alert("Upozornenia zatiaľ nie sú nastavené na serveri."); return; }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") { alert("Upozornenia neboli povolené."); return; }
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(key) });
    await api("/api/push/subscribe", { method: "POST", body: sub.toJSON() });
    await api("/api/push/test", { method: "POST" }).catch(() => {});
    alert("Upozornenia sú zapnuté! 🔔 Mala by prísť testovacia notifikácia.");
  } catch (e) { alert("Upozornenia sa nepodarilo zapnúť."); }
}

// I1 — clear the phone's notification tray when the app comes to the foreground. sw.ts sets no
// tag, so getNotifications() returns every shown push → close them all. Best-effort/silent (like the
// rest of pwa.js); on iOS PWA this is a no-op but harmless.
export async function clearNotifications() {
  try {
    if (!("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    (await reg.getNotifications()).forEach((n) => n.close());
  } catch (e) { /* best-effort, silent */ }
}

// v22.2 — silent re-subscribe on app load: if the user already granted notifications, make sure a
// valid push subscription exists on the server (browsers rotate/expire endpoints; the server prunes
// dead ones on 410, after which the user silently stops getting pushes until they re-subscribe).
export async function ensurePushSubscribed() {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)
      || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    if (isIOS && !isStandalone()) return;
    const { key, enabled } = await api("/api/push/key");
    if (!enabled || !key) return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
      // v26.3 §7 — ak subscription vznikla so STARÝM VAPID kľúčom (po rotácii), je na Apple
      // trvalo mŕtva (HTTP 400 navždy). Odhlás ju a subscribni znova aktuálnym kľúčom.
      const curKey = sub.options && sub.options.applicationServerKey;
      if (curKey && bufToB64Url(curKey) !== key) {
        try { await api("/api/push/unsubscribe", { method: "POST", body: { endpoint: sub.endpoint } }); } catch (e) { /* */ }
        try { await sub.unsubscribe(); } catch (e) { /* */ }
        sub = null;
      }
    }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(key) });
    await api("/api/push/subscribe", { method: "POST", body: sub.toJSON() });
  } catch (e) { /* best-effort, silent */ }
}
